import {useEffect, useState} from 'react';
import {useInView} from 'react-intersection-observer';

import ExtraQuantitySelect from '../ExtraQuantitySelect';
import Accordion from '../Accordion';

import {cn} from '@/lib/utils';
import '../AmenitiesSection/styles.css';
import useObserverContext from '@/hooks/useObserverContext';
import {IVillageDictionary} from '@/dictionaries/pages/village/types';
import {ExtraInfos, FeatureNote, VillageExtra} from '@/services/types';
import useVillageContext from '@/hooks/useVillageContext';
import {IExtraQuantitySelectDictionary} from '@/dictionaries/components/extraQuantitySelect/types';
import useCurrency from '@/hooks/useCurrency';
import useVillage from '@/hooks/useVillage';
import translateFromArray from '@/utils/get-translated-object';

type Dictionaries = {
  villageDict: IVillageDictionary;
  extraQuantitySelectDict: IExtraQuantitySelectDictionary;
};

type Props = {
  dicts: Dictionaries;
  translatedFeatures: FeatureNote[];
  featuresNotes: FeatureNote[];
  extras: VillageExtra[];
  lang: string;
};

export default function ExtrasAndPriceIncludedContent({
  dicts,
  translatedFeatures,
  featuresNotes,
  extras,
  lang,
}: Props) {
  const {
    handleExtras,
    extras: contextExtras,
    selectedDates,
  } = useVillageContext();
  const [extrasIsOpen, setExtrasIsOpen] = useState(true);
  const [featuresIsOpen, setFeaturesIsOpen] = useState(true);
  const {availability} = dicts.villageDict;
  const {minCharge, nights, toBePaidLocally} = dicts.extraQuantitySelectDict;

  const {handleCurrentSection} = useObserverContext();
  const {ref, inView} = useInView();
  const {currency} = useCurrency();
  const {countNights} = useVillage({});

  let noteNumber = 0;

  const currentDate = new Date();

  const targetYear = currentDate.getFullYear();

  const onRequestExtras = extras.filter(
    (extra) =>
      extra.deletedAt === null &&
      extra.villaPricing !== null &&
      extra.villaPricing.year === targetYear &&
      extra.availability === 'on_request',
  );

  const orderedExtras = [
    ...extras.filter(
      (extra) =>
        extra.extra.hasQuantity &&
        extra.deletedAt === null &&
        extra.villaPricing !== null &&
        extra.villaPricing.year === targetYear &&
        extra.availability === 'present',
    ),
    ...extras.filter(
      (extra) =>
        !extra.extra.hasQuantity &&
        extra.deletedAt === null &&
        extra.villaPricing !== null &&
        extra.villaPricing.year === targetYear &&
        extra.availability === 'present',
    ),
  ];

  const extrasWithInfo = orderedExtras.map((extra) => {
    const canAddToNoteNumber = extra.minNights > 0 || extra.payInVilla;

    const label = translateFromArray({
      lang,
      translations: extra.extra.translate,
    });

    const featureNoteNumber = canAddToNoteNumber ? ++noteNumber : 0;

    const infos: ExtraInfos = {
      label: label?.title || '',
      hasQuantity: extra.extra.hasQuantity,
      price: Number(extra.price),
      isFlatPrice: extra.isFlatPrice,
      maxQuantity: extra.maxQuantity,
      freeQuantity: extra.freeQuantity,
      minNights: extra.minNights,
      payInVilla: extra.payInVilla,
      noteNumber: featureNoteNumber,
    };
    return {
      extra,
      infos,
    };
  });

  function handleExtraOnChange(quantity: number, extra: VillageExtra) {
    const extraId = extra.id;
    const freeQuantity = extra.freeQuantity;
    const formatedPrice = Math.round(Number(extra.price));
    const realQuantity = quantity - freeQuantity;

    let computedPrice = formatedPrice * realQuantity;

    if (!extra.isFlatPrice) {
      const days = Math.max(countNights(selectedDates), extra.minNights);
      computedPrice = Math.round(Number(extra.price) * days) * realQuantity;
    }

    if (quantity === 0) {
      return handleExtras(contextExtras.filter(({id}) => id !== extraId));
    }
    const foundExtra = contextExtras.find(({id}) => id === extraId);
    if (foundExtra) {
      const filteredExtras = contextExtras.filter(({id}) => id !== extraId);
      handleExtras([
        ...filteredExtras,
        {...foundExtra, selectedQuantity: quantity, computedPrice},
      ]);
    } else {
      handleExtras([
        ...contextExtras,
        {...extra, selectedQuantity: quantity, freeQuantity, computedPrice},
      ]);
    }
  }

  useEffect(() => {
    if (inView) {
      handleCurrentSection('prices');
    } else {
      handleCurrentSection('');
    }
  }, [inView]);

  return (
    <div>
      <div ref={ref} className="border-b-2 border-gray-200 py-[20px]">
        <Accordion
          label={availability.extras}
          isOpen={extrasIsOpen}
          toggle={() => setExtrasIsOpen((prev) => !prev)}>
          <div className="flex flex-wrap">
            <div className="w-full flex-[0_0_auto] mb-8 desktop:w-3/4 desktop:ml-[25%]">
              <div>
                {extrasWithInfo.map(({extra, infos}, index) => {
                  return (
                    <ExtraQuantitySelect
                      key={index}
                      id={`extra-${index}`}
                      onChange={(quantity) =>
                        handleExtraOnChange(quantity, extra)
                      }
                      extra={infos}
                      lang={lang}
                      index={index}
                      dict={dicts.extraQuantitySelectDict}
                      currencySymbol={currency.symbol}
                    />
                  );
                })}
                <div className="mt-8">
                  {extrasWithInfo.map(({extra, infos}, index) => {
                    if (extra.minNights > 0 || extra.payInVilla) {
                      return (
                        <div className="w-full min-h-6 min-w-0" key={index}>
                          <p className="text-xs text-blue leading-[1.375]">
                            <span className="tabular-nums">
                              {infos.noteNumber}
                            </span>

                            {extra.minNights > 0 && (
                              <span className="text-gray-700">
                                {` ${minCharge} ${extra.minNights} ${nights}`}
                              </span>
                            )}
                            {extra.payInVilla && extra.minNights > 0 && (
                              <span className="text-gray-700">{` - `}</span>
                            )}
                            {extra.payInVilla && (
                              <span className="text-gray-700">
                                {toBePaidLocally}
                              </span>
                            )}
                          </p>
                        </div>
                      );
                    }
                  })}
                </div>
                <hr className="w-full my-5 border-0 max-w-default" />
                {onRequestExtras.length > 0 && (
                  <div className="mt-12">
                    <span className="text-body font-semibold">
                      {availability.onRequestAfterBooking}
                    </span>
                    <div className="">
                      <ul className=" list-none my-2 leading-[1.6]">
                        {onRequestExtras.map(({extra}) => {
                          const translatedExtra = extra.translate.find(
                            (translate) => translate.locale === lang,
                          );
                          return (
                            <li className="mb-2" key={extra.id}>
                              <span>{translatedExtra?.title}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Accordion>
      </div>
      <div className="py-[20px]">
        <Accordion
          label={availability.priceIncludes}
          isOpen={featuresIsOpen}
          toggle={() => setFeaturesIsOpen((prev) => !prev)}
          childrenContainerClassName="pl-8">
          <div className="flex flex-wrap">
            <div className="w-full flex-[0_0_auto] desktop:w-3/4 desktop:ml-[25%]">
              <div className="desktop-column pt-[32px] columns-1">
                <ul className="list-none mb-4 leading-[1.6]">
                  {translatedFeatures.map((feature, index) => {
                    return (
                      <li
                        key={`${index}-${feature.description}`}
                        className="mb-2">
                        <span>{feature.description}</span>
                        {feature.noteNumber && (
                          <sup className="text-xs text-blue ml-1">
                            {feature.noteNumber}
                          </sup>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="mt-8">
                {featuresNotes.map((note, index) => (
                  <p key={`${index}-${note.description}`}>
                    <span className="text-xs text-blue mr-1">
                      {note.noteNumber}
                    </span>
                    <span className="text-xs text-gray-700">
                      {note.description}
                    </span>
                  </p>
                ))}
              </div>
            </div>
          </div>
        </Accordion>
      </div>
    </div>
  );
}
